/**
 * Purchase feedback for the Crimson Dust cards: a burst of crystals grows out of the card's edges, holds for a beat,
 * then shrinks away. The crystals live on their own fixed layer on <body> (positioned from the card's rectangle), so
 * the shop re-rendering right after the purchase does not cut the effect short.
 */

export const CRYSTAL_BURST_MS = 2400;
const SPRITES = ['/crystals/crystal-1.webp', '/crystals/crystal-2.webp', '/crystals/crystal-3.webp', '/crystals/crystal-4.webp', '/crystals/crystal-5.webp'];
const COUNT = 14;

function reducedMotion(): boolean {
  try {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches || document.documentElement.dataset.reduceMotion === 'true';
  } catch {
    return false;
  }
}

/** Points on the card's perimeter, each with the outward direction the crystal should lean toward. */
function perimeterPoint(i: number, w: number, h: number, rand: () => number): { x: number; y: number; lean: number } {
  const side = i % 4; // top, right, bottom, left — spread evenly, so no side is bare
  const t = rand();
  if (side === 0) return { x: t * w, y: 0, lean: -12 + rand() * 24 };
  if (side === 1) return { x: w, y: t * h, lean: 20 + rand() * 25 };
  if (side === 2) return { x: t * w, y: h, lean: -12 + rand() * 24 };
  return { x: 0, y: t * h, lean: -45 + rand() * 25 };
}

/** Sprouts the burst around `card`. Returns the layer (removed automatically), or null when motion is reduced. */
export function crystalBurst(card: HTMLElement, rand: () => number = Math.random): HTMLElement | null {
  if (reducedMotion()) return null;
  const r = card.getBoundingClientRect();
  const layer = document.createElement('div');
  layer.className = 'cc-burst-layer';
  layer.setAttribute('aria-hidden', 'true');
  layer.style.cssText = `left:${r.left}px;top:${r.top}px;width:${r.width}px;height:${r.height}px`;
  for (let i = 0; i < COUNT; i++) {
    const p = perimeterPoint(i, r.width, r.height, rand);
    const img = document.createElement('img');
    img.className = 'cc-burst';
    img.src = SPRITES[Math.floor(rand() * SPRITES.length)];
    img.alt = '';
    img.draggable = false;
    const size = 34 + rand() * 46; // px tall
    img.style.cssText = `left:${p.x}px;top:${p.y}px;height:${size}px;--lean:${p.lean}deg;--d:${Math.round(rand() * 420)}ms`;
    layer.appendChild(img);
  }
  document.body.appendChild(layer);
  window.setTimeout(() => layer.remove(), CRYSTAL_BURST_MS);
  return layer;
}
